<template>
	<div>
		<!-- This itself is aligned z-index 0 so that it goes below the product image it's used on,
        but all its sub-components should have a higher z-index to go on top of the product image -->
		<div class="absolute inset-0 grid grid-cols-2 grid-rows-2 p-md">
			<!-- Top left, promotional tags, attribute tags and product badges -->
			<div
				class="z-20 col-start-1 row-start-1 -m-2 flex flex-col items-start space-y-xs self-start p-2"
			>
				<!-- Promotional tag -->
				<v-page-product-sale-tag
					v-if="promotion !== undefined && promotionTheme"
					:promotion="promotion"
					:price="price"
					:original-price="originalPrice"
					:promotion-price="promotionPrice"
					:club-price="clubPrice"
					:can-show-club-price="canShowClubPrice"
				/>

				<!-- Attribute tags -->
				<template v-if="attributesData">
					<template v-for="attribute in attributesData" :key="attribute.id">
						<v-product-card-tag v-if="attribute.value" :tag="attribute.value" />
					</template>
				</template>

				<!-- Product badges -->
				<template
					v-for="badge in productBadges.filter(
						(badge) => badge.position === ProductBadgePosition.TopLeft,
					)"
					:key="badge.id"
				>
					<v-page-product-badge-matrix-badge :badge="badge" />
				</template>
			</div>

			<!-- Top right, product badges-->
			<div
				class="z-20 col-start-2 row-start-1 -m-2 flex flex-col items-end space-y-xs self-start p-2"
			>
				<template
					v-for="badge in productBadges.filter(
						(badge) =>
							badge.position === ProductBadgePosition.TopRight ||
							badge.position === null,
					)"
					:key="badge.id"
				>
					<v-page-product-badge-matrix-badge :badge="badge" />
				</template>
			</div>

			<!-- Bottom left, product badges and brand logo -->
			<div
				class="z-20 col-start-1 row-start-2 -m-2 flex flex-col items-start space-y-xs self-end p-2 pb-sm md:pb-0"
			>
				<!-- Brand logo -->
				<template v-if="brand">
					<v-page-product-brand-logo
						v-if="brand.logo"
						:key="brand.title"
						:brand="brand"
					/>

					<template v-else>
						<v-product-card-tag
							v-if="!brand.logo && brand.title"
							:tag="brand.title"
						/>
					</template>
				</template>

				<template
					v-for="badge in productBadges.filter(
						(badge) => badge.position === ProductBadgePosition.BottomLeft,
					)"
					:key="badge.id"
				>
					<v-page-product-badge-matrix-badge :badge="badge" />
				</template>
			</div>

			<!-- Bottom right, product badges -->
			<div
				class="z-20 col-start-2 row-start-2 -m-2 flex flex-col items-end space-y-xs self-end p-2 pb-sm md:pb-0"
			>
				<template
					v-for="badge in productBadges.filter(
						(badge) => badge.position === ProductBadgePosition.BottomRight,
					)"
					:key="badge.id"
				>
					<v-page-product-badge-matrix-badge :badge="badge" />
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	ProductBadgePosition,
	type ViewProductDetailsQuery,
} from "@pkg/shared/graphql";

type DetailProduct = Extract<
	ViewProductDetailsQuery["product"],
	{__typename: "SimpleProduct"}
>;

type ProductBadge = DetailProduct["productBadges"];
type Promotion = DetailProduct["promotions"][0];

interface AttributeData {
	id: string;
	title: string;
	value?: string;
}

interface BrandData {
	logo: string;
	title?: string;
}

const properties = defineProps<{
	productBadges: ProductBadge;
	price?: string;
	originalPrice?: string;
	promotionPrice?: string;
	clubPrice?: number;
	canShowClubPrice: boolean;
	promotion?: Promotion | undefined;
	attributesData: AttributeData[];
	brand?: BrandData;
}>();

const promotionTheme = computed(() => {
	if (
		properties.promotion?.__typename === "CatalogPromotionPercent" ||
		properties.promotion?.__typename === "CatalogPromotionFixedAmount" ||
		properties.promotion?.__typename === "CatalogPromotionProductPromotionPrice"
	) {
		const {style} = properties.promotion;

		return {
			mainTheme: style?.mainTheme,
			altTheme: style?.altTheme,
		};
	}
	return undefined;
});
</script>
